/**
 * Workspace-name field module — modular-form field for workspace name.
 *
 * Conditionally required based on businessNumberCountry and entityType.
 * When both are present, the field is required and must not be empty.
 * When either is absent, the field is nullable.
 */
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sr/ui-library';
import { Control } from 'react-hook-form';
import * as yup from 'yup';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';

interface WorkspaceNameFieldProps {
  control: Control<WorkspaceDetailsFormValues>;
}

export const WorkspaceNameField = ({ control }: WorkspaceNameFieldProps) => (
  <FormField
    name={WORKSPACE_DETAILS_FORM_FIELDS.workspaceName}
    control={control}
    render={({ field }) => (
      <FormItem>
        <FormLabel
          asterisk
          tooltipContent="Please choose a Workspace Name that is easy to identify for you and your team members. This could be a trading name or other unique display name that is easy to distinguish from other workspaces."
        >
          Workspace Name
        </FormLabel>
        <FormControl>
          <Input {...field} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

WorkspaceNameField.schema = yup
  .string()
  .when(
    ['businessNumberCountry', 'entityType'],
    ([businessNumberCountry, entityType], schema) => {
      if (!businessNumberCountry || !entityType) {
        return schema.nullable();
      }

      return schema
        .required('Please enter a Workspace Name')
        .test('is-not-empty', 'Please enter a valid Workspace Name', (value) =>
          Boolean(value?.trim().length),
        );
    },
  );
